import React, { useContext } from 'react';
import { Panel } from '../../../components/containers/Panel';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { Toggle } from '../../../components/forms/Toggle';
import { GetEmailNotificationSettingsLazyQueryHookResult, UserOrganizationRole } from '../../../models/graphql';
import { GetMe_me_settings } from '../../../models/legacy-graphql';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type EmailNotificationViewProps = {
  userSettings: GetMe_me_settings | undefined;
  selectedOrgRole: UserOrganizationRole | null;
  formInstance: UseFormReturn;
  rules: RegisterOptions;
  toggleRules: RegisterOptions;
  isLoading: boolean;
  getEmailNotificationSettingsResults: GetEmailNotificationSettingsLazyQueryHookResult[1];
  notifySubscriptionEnabledHandler: ToggleAction;
  notifyQueueEmptyEnabledHandler: ToggleAction;
  notifyAutoRssEnabledHandler: ToggleAction;
  notifyPostProfileErrorEnabledHandler: ToggleAction;
  notifyWeeklyStatsEnabledHandler: ToggleAction;
  notifyInstagramPostEnabledHandler: ToggleAction;
  notifyMailingListEnabledHandler: ToggleAction;
  notifyProductUpdatesEnabledHandler: ToggleAction;
  notifyReferralCreditsEnabledHandler: ToggleAction;
  notifyFeatureAddedEnabledHandler: ToggleAction;
};

export const EmailNotificationView = (props: EmailNotificationViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const settings = props.getEmailNotificationSettingsResults.data?.organization;

  return (
    <CenteredContentLayout loading={props.isLoading} headerTitle={'Emails & Notifications'} hasSidebar>
      <Panel>
        <PanelHeader
          title={'Send me an email notification if:'}
          subTitle={'These settings affect your account in all Workspaces within this Organization.'}
        />
        <div className={'grid-x align-justify margin-bottom-small-24'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyQueueEmptyEnabled'}
            label={'One of my Queues runs out of fresh posts'}
            helper={'This ignores any Evergreen content in your Queue.'}
            action={(value, complete) => props.notifyQueueEmptyEnabledHandler(value, complete)}
            checked={settings?.myUserAssignment?.settings.notifyQueueEmptyEnabled}
          />
        </div>
        <div className={'grid-x align-justify margin-bottom-small-24'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyPostProfileErrorEnabled'}
            label={'There is a problem with a profile/post'}
            helper={'If a profile gets disconnected, or a post fails to be published.'}
            action={(value, complete) => props.notifyPostProfileErrorEnabledHandler(value, complete)}
            checked={settings?.myUserAssignment?.settings.notifyPostProfileErrorEnabled}
          />
        </div>
        <div className={'grid-x align-justify margin-bottom-small-24'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyInstagramPostEnabled'}
            label={'An Instagram post is ready to be posted'}
            helper={
              <>
                <div>
                  <p className={'no-b-m small-text'}>
                    Whether your Instagram posts will be published directly, or you&apos;ve enabled mobile push
                    notifications, you can choose to receive an additional email notification as a backup or reminder.
                  </p>
                </div>
                <div>
                  <p className={'no-b-m small-text'}>
                    Note: If your Instagram post cannot be published directly or via mobile notification, you will
                    always receive an email notification regardless of this setting.{' '}
                    <a onClick={() => beacon('article', '721', { type: 'modal' })}>Learn more</a>
                  </p>
                </div>
              </>
            }
            action={(value, complete) => props.notifyInstagramPostEnabledHandler(value, complete)}
            checked={settings?.myUserAssignment?.settings.notifyInstagramPostEnabled}
          />
        </div>
        <div className={'grid-x align-justify margin-bottom-small-24'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyAutoRssEnabled'}
            label={'There is a problem with an RSS Auto-Importer'}
            helper={'If an RSS Auto-Importer fails to import new posts.'}
            action={(value, complete) => props.notifyAutoRssEnabledHandler(value, complete)}
            checked={settings?.myUserAssignment?.settings.notifyAutoRssEnabled}
          />
        </div>
        <div className={'grid-x align-justify margin-bottom-small-24'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyWeeklyStatsEnabled'}
            label={'Weekly stats'}
            helper={
              'Get an overview email for all Profiles in this Workspace, showing a summary of your engagement and growth for the last two weeks.'
            }
            action={(value, complete) => props.notifyWeeklyStatsEnabledHandler(value, complete)}
            checked={settings?.myUserAssignment?.settings.notifyWeeklyStatsEnabled}
          />
        </div>
      </Panel>
      <Panel>
        <PanelHeader
          title={'My Account'}
          subTitle={'These settings affect your account in all Workspaces across all Organizations.'}
        />
        <div className={'grid-x align-justify margin-bottom-small-24'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyProductUpdatesEnabled'}
            label={'Product updates'}
            helper={'Occasional emails with information about new features and improvements.'}
            action={(value, complete) => props.notifyProductUpdatesEnabledHandler(value, complete)}
            checked={props.userSettings?.notifyProductUpdatesEnabled}
          />
        </div>
        <div className={'grid-x align-justify margin-bottom-small-32'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyMailingListEnabled'}
            label={'Mailing list'}
            helper={'Occasional emails with social media tips, special offers.'}
            action={(value, complete) => props.notifyMailingListEnabledHandler(value, complete)}
            checked={props.userSettings?.notifyMailingListEnabled}
          />
        </div>
        <div className={'grid-x align-justify margin-bottom-small-32'}>
          <Toggle
            rules={props.toggleRules}
            formInstance={props.formInstance}
            name={'notifyReferralCreditsEnabled'}
            label={'Referral commission earned'}
            helper={'If someone signs up for a paid subscription via your referral code.'}
            action={(value, complete) => props.notifyReferralCreditsEnabledHandler(value, complete)}
            checked={props.userSettings?.notifyReferralCreditsEnabled}
          />
        </div>
      </Panel>
    </CenteredContentLayout>
  );
};
